import {useContext, useEffect, useMemo, useRef, useState} from "react";

// react-router components
import {Route, Routes, useLocation, useNavigate, useSearchParams} from "react-router-dom";
import {AppContext} from "./lib/AppContext";

// @mui material components
import CssBaseline from "@mui/material/CssBaseline";

// Material Dashboard 2 PRO React examples
import Sidenav from "./app/Sidenav";

// Material Dashboard 2 PRO React themes
// Material Dashboard 2 PRO React Dark Mode themes
// RTL plugins
import rtlPlugin from "stylis-plugin-rtl";
import createCache from "@emotion/cache";

// Material Dashboard 2 PRO React routes
import routes from "./routes";

// Material Dashboard 2 PRO React contexts
import {setMiniSidenav, setOpenConfigurator, useMaterialUIController} from "./context";

// Images
import brandWhite from "./assets/images/logo-ct.png";
import brandDark from "./assets/images/logo-ct-dark.png";
import {CONSTANTS} from "./app/common/constants";
import Loading from "./app/common/Loading";
import Client from "./app/client/Client";
import Company from "./app/companies/Company";
import NewCompany from "./app/companies/NewCompany";
import Logout from "./app/Logout";
import Init from "./app/Init";
import LoginPage from "./app/LoginPage";
import {isSessionClient, isSessionPending, isSessionSuperAdmin} from "./app/common/Utils";
import ViewEmployee from "./app/employee/ViewEmployee";
import CommunicationTemplates from "./app/company/CommunicationTemplates";
import ComposeEmail from "./app/email/ComposeEmail";
import ManageInventoryEvent from "./app/inventory/ManageInventoryEvent";
import {Modal} from "react-bootstrap";
import MDButton from "./components/MDButton";
import WaiverManager from "./app/company/WaiverManager";
import Toast from "./app/common/Toast";
import ViewInventoryEvent from "./app/inventory/ViewInventoryEvent";
import ViewInventory from "./app/inventory/ViewInventory";
import logoSm from "./app/assets/images/1x/psb-2025-space-logo-sm.png";

import TermsOfService from "./app/company/TermsOfService";
import ReportDetails from "./app/reports/ReportDetails";
import CustomerReportDetails from "./app/reports/CustomerReportDetails";
import VisitsReportDetails from "./app/reports/VisitsReportDetails";
import BillingReportDetails from "./app/reports/BillingReportDetails";
import UpcomingReportDetails from "./app/reports/UpcomingBillingReportDetails";
import PartiesReportDetails from "./app/reports/PartiesReportDetails";
import Privacy from "./app/company/Privacy";
import CustomersDetails from "./app/reports/CustomersDetails";
import EventTemplates from "./app/company/EventTemplates";
import GenericReport from "./app/reports/GenericReport";
import SalesReportDetails from "./app/reports/SalesReportDetails";
import CheckinReport from "./app/reports/CheckinReport";
import {CompanyContext} from "./lib/CompanyContext";
import CafeOptions from "./app/company/CafeOptions";
import useModalWatcher from "./lib/useModalWatcher";
import PublicReleaseNotes from "./app/company/PublicReleaseNotes";
import {LastViewProvider} from "./lib/LastViewProvider";
import {useKiosk} from "./lib/KioskModeProvider";
import MemberBillingCalendar from "./app/reports/MemberBillingCalendar";
import TipReport from "./app/reports/TipReport";
import {useAccess} from "./lib/AccessProvider";
import {switchLocation} from "./app/common/api/WebApi";
import OrganizationCustomersReport from "./app/reports/OrganizationCustomersReport";
import ActivityReport from "./app/reports/ActivityReport";

export default function App() {
  const [controller, dispatch] = useMaterialUIController();
  const {
    miniSidenav,
    miniWindow,
    direction,
    layout,
    openConfigurator,
    sidenavColor,
    transparentSidenav,
    whiteSidenav,
    darkMode,
  } = controller;
  const [onMouseEnter, setOnMouseEnter] = useState(false);
  const [rtlCache, setRtlCache] = useState(null);
  const {pathname} = useLocation();
  const [searchParams, setSearchParams] = useSearchParams();

  const {toggleKioskMode} = useKiosk();
  const location = useLocation();
  const {getAccess} = useAccess();

  const [orgAccess, setOrgAccess] = useState(null);
  const [orgSwitching, setOrgSwitching] = useState(false);
  const [context, setContext] = useContext(AppContext);
  const [companyContext, setCompanyContext] = useContext(CompanyContext);

  const [switchCompanyLocation, setSwitchCompanyLocation] = useState(false);

  const [company, setCompany] = useState();
  useEffect(() => {
    setCompany(companyContext?.company);
  }, [companyContext?.company]);

  useEffect(() => {
    const fragment = location.hash.slice(1);
    if ('switch-location' === fragment) {
      let access = getAccess();
      if (access?.ready && access?.orgOwner) {
        setOrgAccess(access);
        setSwitchCompanyLocation(true);
      }
    }
  }, [location, company]);

  const navigate = useNavigate();

  const [sessionLoggedIn, setSessionLoggedIn] = useState(false);
  useEffect(() => {
    if (!context?.sessionUser) {
      return;
    }
    setSessionLoggedIn(context?.sessionUser?.guid?.length > 0);
  }, [context?.sessionUser]);

  const [collapse, setCollapse] = useState(false);
  const [hasSidebar, setHasSidebar] = useState(false);
  const [isCustomer, setIsCustomer] = useState(false);
  const [appReady, setAppReady] = useState(false);
  const [noSessionSidenav, setNoSessionSidenav] = useState(false);

  useEffect(() => {

    if (!context?.sessionUser) {
      return;
    }
    setNoSessionSidenav(isSessionClient(context?.sessionUser) && !context?.sessionUser?.guid);
    setIsCustomer(context?.sessionUser?.userTypeId === CONSTANTS.UTYPE_CUSTOMER);
    setHasSidebar(isSessionClient(context?.sessionUser) || isSessionSuperAdmin(context?.sessionUser));

    window.setTimeout(function () {
      setAppReady(true);
    }, 1);

  }, [context?.sessionUser]);

  useEffect(() => {
    // setNoSessionSidenav(hasSidebar);
  }, [appReady]);

  // Cache for the rtl
  useMemo(() => {
    const cacheRtl = createCache({
      key: "rtl",
      stylisPlugins: [rtlPlugin],
    });

    setRtlCache(cacheRtl);
  }, []);

  // Open sidenav when mouse enter on mini sidenav
  const handleOnMouseEnter = () => {
    if (miniSidenav && !onMouseEnter) {
      setMiniSidenav(dispatch, false);
      setOnMouseEnter(true);
    }
  };

  // Close sidenav when mouse leave mini sidenav
  const handleOnMouseLeave = () => {
    if (onMouseEnter) {
      setMiniSidenav(dispatch, true);
      setOnMouseEnter(false);
    }
  };

  // Change the openConfigurator state
  const handleConfiguratorOpen = () => setOpenConfigurator(dispatch, !openConfigurator);

  // Setting the dir attribute for the body element
  useEffect(() => {
    document.body.setAttribute("dir", direction);
  }, [direction]);

  // Setting page scroll to 0 when changing the route
  useEffect(() => {
    document.documentElement.scrollTop = 0;
    document.scrollingElement.scrollTop = 0;
  }, [pathname]);

  const getRoutes = (allRoutes) => {
    return allRoutes.map((route) => {
      if (route.collapse) {
        return getRoutes(route.collapse);
      }

      if (route.route) {
        return <Route exact path={route.route} element={route.component} key={route.key}/>;
      }

      return null;
    });
  }

  useModalWatcher();

  const prevPathRef = useRef(location.pathname);
  const {pullClient} = useKiosk();

  useEffect(() => {
    const prevPath = prevPathRef.current;
    const currentPath = location.pathname;
    pullClient();
    prevPathRef.current = currentPath;
  }, [location.pathname]);

  const switchLocationFn = (org) => {
    setOrgSwitching(true);
    switchLocation(org).then((resp) => {
      let _location = location.pathname.split('#')[0];
      navigate(_location);
      setOrgSwitching(false);
      setTimeout(() => {
        window.location.reload();
      }, 100);
    });
  }

  return (
    <>
      <LastViewProvider company={companyContext?.company}>

        <div>
          {miniWindow && miniSidenav && <div style={{
            width: '100%',
            display: 'block'
          }}>
            <MDButton style={{
              margin: '2px',
              display: 'inline-block',
              background: 'transparent'
            }}>
              <img
                onClick={(e) => {
                  navigate('/');
                }}
                style={{
                  width: '35px',
                  height: 'auto',
                }}
                src={logoSm} className={'logo-sm logo'}/>
              {sessionLoggedIn && <span className={'fa fa-bars'}
                                        onClick={(e) => {
                                          setMiniSidenav(dispatch, false);
                                        }}
                                        style={{
                                          fontSize: '16px',
                                          marginLeft: '5px'
                                        }}></span>}
            </MDButton>
          </div>
          }
        </div>

        <Toast/>

        <Modal show={context?.error} onHide={() => setContext({...context, error: null})}>
          <Modal.Header closeButton>
            <Modal.Title>Error</Modal.Title>
          </Modal.Header>
          <Modal.Body>{context?.error && context.error.message}</Modal.Body>
          <Modal.Footer>
            <MDButton color="info" onClick={() => setContext({...context, error: null})}>
              Close
            </MDButton>
          </Modal.Footer>
        </Modal>

        <Modal show={orgAccess?.ready && switchCompanyLocation} onHide={() => {
          setSwitchCompanyLocation(false);
        }}>
          <Modal.Header closeButton>
            <Modal.Title>Switch Location</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            <table className={'table table-striped table-hover'}>
              <tbody>
              {orgAccess?.orgChart?.parentOrganization && <tr className={'my-2'}>
                <td sm={8}>
                  {orgAccess?.orgChart?.parentOrganization.organizationName}
                </td>
                <td className={'text-right'} sm={4}>
                  <MDButton disabled={orgSwitching} className={'pull-right'} size="small" color="info" onClick={() => {
                    switchLocationFn(orgAccess?.orgChart?.parentOrganization)
                  }}>
                    Switch
                  </MDButton>
                </td>
              </tr>}
              {orgAccess?.orgChart?.childOrganizations?.map((org, idx) => {
                return <tr key={idx} className={'my-2'}>
                  <td sm={8}>
                    {org.organizationName}
                  </td>
                  <td className={'text-right'} sm={4}>
                    <MDButton disabled={orgSwitching} className={'pull-right'} size="small" color="info"
                              onClick={() => {
                                switchLocationFn(org);
                              }}>
                      Switch
                    </MDButton>
                  </td>
                </tr>
              })}
              {orgAccess?.orgChart?.siblingOrganizations?.map((org, idx) => {
                return <tr key={idx} className={'my-2'}>
                  <td sm={8}>
                    {org.organizationName}
                  </td>
                  <td className={'text-right'} sm={4}>
                    <MDButton disabled={orgSwitching} className={'pull-right'} size="small" color="info"
                              onClick={() => {
                                switchLocationFn(org);
                              }}>
                      Switch
                    </MDButton>
                  </td>
                </tr>
              })}
              </tbody>
            </table>
          </Modal.Body>
          <Modal.Footer>
            <MDButton color="info" onClick={() => {
              setSwitchCompanyLocation(false);
            }}>
              Close
            </MDButton>
          </Modal.Footer>
        </Modal>

        <CssBaseline/>

        {appReady && <>
          {layout === "dashboard" && (
            <>
              {hasSidebar && <>
                <Sidenav
                  color={sidenavColor}
                  brand={(transparentSidenav && !darkMode) || whiteSidenav ? brandDark : brandWhite}
                  brandName="PSB"
                  routes={routes}
                  onMouseEnter={handleOnMouseEnter}
                  onMouseLeave={handleOnMouseLeave}
                />
              </>
              }

              {noSessionSidenav && <>
                <Sidenav
                  color={sidenavColor}
                  brand={(transparentSidenav && !darkMode) || whiteSidenav ? brandDark : brandWhite}
                  brandName="PSB"
                  routes={[]}
                  onMouseEnter={handleOnMouseEnter}
                  onMouseLeave={handleOnMouseLeave}
                />
              </>
              }

            </>
          )}

          <Routes>

            <Route exact path="/terms-of-service" element={<TermsOfService/>}/>
            <Route exact path="/privacy" element={<Privacy/>}/>

            {isSessionPending(context?.sessionUser) && <Route path="/" element={<Loading/>}/>}

            {getRoutes(routes)}

            {/*<Route path="*" element={<Navigate to="/login"/>}/>*/}

            <Route path="/init" element={<Init/>}/>

            <Route path="/login" element={<LoginPage/>}/>

            <Route path="/client/:guid" element={<Client/>}/>

            {isSessionSuperAdmin(context?.sessionUser) && <Route exact path="/company/:guid" element={<Company />}/>}
            {isSessionSuperAdmin(context?.sessionUser) && <Route exact path="/company/new" element={<NewCompany />}/>}

            <Route path="/event/manage/:guid" element={<ManageInventoryEvent/>}/>
            <Route path="/event/view/:guid" element={<ViewInventoryEvent/>}/>
            <Route path="/inventory/view/:guid" element={<ViewInventory/>}/>

            <Route path="/sales/create/event" element={<ManageInventoryEvent/>}/>
            <Route path="/sales/create/party" element={<ManageInventoryEvent party={true}/>}/>

            <Route path="/communication/templates/:guid" element={<CommunicationTemplates/>}/>
            <Route path="/event/templates/:guid" element={<EventTemplates/>}/>

            <Route path="/communication/waivers/:guid" element={<WaiverManager/>}/>
            <Route exact path="/company/employees/view/:guid" element={<ViewEmployee/>}/>

            <Route exact path="/company/release-notes" element={<PublicReleaseNotes/>}/>

            <Route exact path="/cafe/cafe-groups/:guid" element={<CafeOptions/>}/>

            <Route exact path="/email/client/:guid" element={<ComposeEmail/>}/>
            <Route exact path="/email/clients/:guids" element={<ComposeEmail multiple={true}/>}/>

            <Route path="/data/report/:reportYear/:reportMonth" element={<GenericReport type={'generic'}/>}/>

            <Route path="/birthdays/report/:reportYear/:reportMonth" element={<GenericReport type={'birthdays'}/>}/>
            <Route path="/tags/report/:criteria" element={<GenericReport type={'tags'} disableCalendar={true}/>}/>
            <Route path="/tags/report/"
                   element={<GenericReport type={'tagsgroup'} disableCalendar={true} all={true}/>}/>

            <Route path="/duplicates/report/"
                   element={<GenericReport type={'duplicates'} disableCalendar={true} all={true}/>}/>

            <Route path="/email/report/"
                   element={<GenericReport type={'email'} disableCalendar={true} all={true}/>}/>

            <Route path="/activity/report/"
                   element={<ActivityReport type={'activity'} disableCalendar={true} all={true}/>}/>

            <Route path="/winback/report/:criteria"
                   element={<GenericReport type={'winback'} disableCalendar={true}/>}/>
            <Route path="/winback/report/" element={<GenericReport type={'winback'} disableCalendar={true}/>}/>

            <Route path="/zipcode/report/:criteria"
                   element={<GenericReport type={'zipcode'} disableCalendar={true}/>}/>
            <Route path="/zipcode/report/"
                   element={<GenericReport type={'zipcodesgroup'} disableCalendar={true} all={true}/>}/>

            <Route path="/checkin/report/"
                   element={<CheckinReport type={'checkin'} range={true} defaultRange={true} defaultRangeStart={-7}
                                           defaultRangeEnd={"0"}/>}/>

            <Route path="/tips/report/"
                   element={<TipReport type={'tips'} range={false} defaultRange={false}/>}/>


            <Route path="/organization-customers/report/"
                   element={<OrganizationCustomersReport  />}/>

            <Route path="/partyage/report/"
                   element={<GenericReport type={'partyage'} range={true} defaultRange={true}
                                           defaultRangeStart={-30} defaultRangeEnd={"0"}/>}/>

            <Route path="/refunds/report/" element={<GenericReport type={'refunds'} range={true}/>}/>
            <Route path="/invoice/report/" element={<GenericReport type={'invoice'} range={true}/>}/>
            <Route path="/inventory/report/" element={<GenericReport type={'inventory'} range={true}/>}/>

            <Route path="/customers/report/:reportYear/:reportMonth"
                   element={<CustomersDetails type={'customers'}/>}/>
            <Route path="/customers/report/:reportYear/:reportMonth/:reportEndYear/:reportEndMonth"
                   element={<CustomersDetails range={true} type={'customers'}/>}/>

            <Route path="/sales/report/today" element={<SalesReportDetails type={'sales'} range={true} daily={true}/>}/>

            <Route path="/sales/report/:reportYear/:reportMonth" element={<SalesReportDetails type={'sales'}/>}/>

            <Route path="/visits/report/:reportYear/:reportMonth" element={<VisitsReportDetails type={'visits'}/>}/>

            <Route path="/giftcard/report/" element={<GenericReport type={'giftcard'} range={true}/>}/>
            <Route path="/giftcardbalance/report/" element={<GenericReport type={'giftcardbalance'} disableCalendar={true}/>}/>

            <Route path="/discountcode/report/" element={<GenericReport type={'discountcode'} range={true}/>}/>

            <Route path="/member/billing/calendar"
                   element={<MemberBillingCalendar/>}/>

            <Route path="/memberships/report/:reportYear/:reportMonth"
                   element={<CustomerReportDetails type={'memberships'} range={false} disableRange={true}
                                                   showRecurringMemberships={true}/>}/>

            <Route path="/passes/report/:reportYear/:reportMonth"
                   element={<CustomerReportDetails range={false} disableRange={true} type={'passes'}
                                                   showRecurringMemberships={false}/>}/>

            <Route path="/parties/report/:reportYear/:reportMonth"
                   element={<PartiesReportDetails type={'parties'}/>}/>

            <Route path="/billing/report/:reportYear/:reportMonth"
                   element={<BillingReportDetails type={'billing'} range={true}/>}/>

            <Route path="/billing/future" element={<UpcomingReportDetails type={'future'} range={true}/>}/>
            <Route path="/billing/future/:reportYear/:reportMonth"
                   element={<UpcomingReportDetails type={'future'} range={true}/>}/>

            <Route path="/sales/report/:reportYear/:reportMonth/:reportEndYear/:reportEndMonth"
                   element={<ReportDetails range={true} type={'sales'}/>}/>

            <Route path="/visits/report/:reportYear/:reportMonth/:reportEndYear/:reportEndMonth"
                   element={<VisitsReportDetails range={true} type={'visits'}/>}/>
            <Route path="/memberships/report/:reportYear/:reportMonth/:reportEndYear/:reportEndMonth"
                   element={<CustomerReportDetails range={true} type={'memberships'}
                                                   showRecurringMemberships={true}/>}/>
            <Route path="/parties/report/:reportYear/:reportMonth/:reportEndYear/:reportEndMonth"
                   element={<PartiesReportDetails range={true} type={'parties'}/>}/>

            <Route path="/logout" element={<Logout/>}/>

          </Routes>
        </>
        }

      </LastViewProvider>
    </>
  );
}
